import apiClient from "../apiClient";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { formatMoney } from "../formatMoney";
import PageContext from "../PageContext.jsx";

const PeriodsUpdate = () => {
    const [period, setPeriod] = useState({
        Description: "",
        DateStart: "",
        DateEnd: "",
        SavingsAmount: "",
        IncomeAmount: "",
        ExpensesAmount: "",
        SavingsIncomeAmount: "",
        LiabilitiesAmount: "",
        Notes: "",
        ActiveYesNo: "0",
        });

    const [error, setError] = useState(false)
    const [importingTotals, setImportingTotals] = useState(false);
    const [currencies, setCurrencies] = useState([]);

    const navigate = useNavigate();
    const location = useLocation();

    const periodId = location.pathname.split("/")[2]
    const defaultCurrency = useMemo(
      () => currencies.find((row) => Number(row.IsDefault))?.Description || "",
      [currencies]
    );

    useEffect(() => {
      const fetchData = async () => {
        try {
          const [periodsRes, currenciesRes] = await Promise.all([
            apiClient.get("/periods"),
            apiClient.get("/currencies"),
          ]);
          const periodRows = Array.isArray(periodsRes.data) ? periodsRes.data : [];
          const currentPeriod = periodRows.find((item) => String(item.PeriodId) === String(periodId));
          if (currentPeriod) {
            setPeriod({
              Description: currentPeriod.Description || "",
              DateStart: currentPeriod.DateStart || "",
              DateEnd: currentPeriod.DateEnd || "",
              SavingsAmount: currentPeriod.SavingsAmount != null ? String(currentPeriod.SavingsAmount) : "",
              IncomeAmount: currentPeriod.IncomeAmount != null ? String(currentPeriod.IncomeAmount) : "",
              ExpensesAmount: currentPeriod.ExpensesAmount != null ? String(currentPeriod.ExpensesAmount) : "",
              SavingsIncomeAmount: currentPeriod.SavingsIncomeAmount != null ? String(currentPeriod.SavingsIncomeAmount) : "",
              LiabilitiesAmount: currentPeriod.LiabilitiesAmount != null ? String(currentPeriod.LiabilitiesAmount) : "",
              Notes: currentPeriod.Notes || "",
              ActiveYesNo: String(currentPeriod.ActiveYesNo ?? "0"),
            });
          }
          setCurrencies(Array.isArray(currenciesRes.data) ? currenciesRes.data : []);
        } catch (err) {
          setError(true);
        }
      };
      fetchData();
    }, [periodId]);

    const handleChange = (e) => {
        setPeriod((prev) => ({ ...prev, [e.target.name]: e.target.value }));
        }

    const handleClick = async(e) => {
        e.preventDefault();
        if (!period.Description?.trim() || !period.DateStart || !period.DateEnd) return setError(true);
        try {
            await apiClient.put("/periods/" + periodId, period);
            navigate("/Periods");
        } catch (err) {
            console.log(err);
            setError(true)
        }
     };

    const handleImportTotals = async () => {
      setImportingTotals(true);
      setError(false);
      try {
        const res = await apiClient.get("/reports/period-summary");
        const rows = Array.isArray(res.data) ? res.data : [];
        const currentTotals = rows.find((row) => String(row.PeriodId) === String(periodId));
        if (!currentTotals) {
          setError(true);
          return;
        }
        setPeriod((prev) => ({
          ...prev,
          IncomeAmount: String(currentTotals.TotalCredits ?? 0),
          ExpensesAmount: String(currentTotals.TotalDebits ?? 0),
          SavingsAmount: String(currentTotals.TotalSavings ?? 0),
          SavingsIncomeAmount: String(currentTotals.TotalPassiveIncome ?? 0),
        }));
      } catch (err) {
        setError(true);
      } finally {
        setImportingTotals(false);
      }
    };
    
    return (
        <div className="page">
            <header className="page-header">
                <div>
                    <h1 className="page-title">Update Period</h1>
                    <p className="page-subtitle"><Link to="/">Home</Link> / <Link to="/Periods">Periods</Link> / Update</p>
                </div>
            </header>
            <PageContext
                items={[
                    { label: "Home", to: "/" },
                    { label: "Periods", to: "/Periods" },
                    { label: "Update Period" },
                ]}
                note={period.Description ? `Editing ${period.Description}.` : "Update the selected reporting period and its historical totals."}
            />
            <section className="panel form-card">
                <div className="form">
                    <label htmlFor="period-description">Description</label>
                    <input id="period-description" type="text" placeholder="Period Description" name="Description" value={period.Description} onChange={handleChange} />
                    <label htmlFor="period-start">Start Date</label>
                    <input id="period-start" type="date" name="DateStart" value={period.DateStart} onChange={handleChange} />
                    <label htmlFor="period-end">End Date</label>
                    <input id="period-end" type="date" name="DateEnd" value={period.DateEnd} onChange={handleChange} />
                    <label htmlFor="period-income">Income</label>
                    <input id="period-income" type="number" step="0.01" name="IncomeAmount" value={period.IncomeAmount} onChange={handleChange} />
                    <div className="form-value-preview">Display: {formatMoney(period.IncomeAmount || 0, defaultCurrency || "Default")}</div>
                    <label htmlFor="period-expenses">Expenses</label>
                    <input id="period-expenses" type="number" step="0.01" name="ExpensesAmount" value={period.ExpensesAmount} onChange={handleChange} />
                    <div className="form-value-preview">Display: {formatMoney(period.ExpensesAmount || 0, defaultCurrency || "Default")}</div>
                    <label htmlFor="period-savings">Savings</label>
                    <input id="period-savings" type="number" step="0.01" name="SavingsAmount" value={period.SavingsAmount} onChange={handleChange} />
                    <div className="form-value-preview">Display: {formatMoney(period.SavingsAmount || 0, defaultCurrency || "Default")}</div>
                    <label htmlFor="period-savings-income">Savings Income</label>
                    <input id="period-savings-income" type="number" step="0.01" name="SavingsIncomeAmount" value={period.SavingsIncomeAmount} onChange={handleChange} />
                    <div className="form-value-preview">Display: {formatMoney(period.SavingsIncomeAmount || 0, defaultCurrency || "Default")}</div>
                    <label htmlFor="period-liabilities">Liabilities</label>
                    <input id="period-liabilities" type="number" step="0.01" name="LiabilitiesAmount" value={period.LiabilitiesAmount} onChange={handleChange} />
                    <div className="form-value-preview">Display: {formatMoney(period.LiabilitiesAmount || 0, defaultCurrency || "Default")}</div>
                    <label htmlFor="period-notes">Notes</label>
                    <textarea
                        id="period-notes"
                        name="Notes"
                        rows="3"
                        value={period.Notes}
                        onChange={handleChange}
                        placeholder="Optional notes for this period"
                    />
                    <label htmlFor="period-active">Active</label>
                    <select id="period-active" name="ActiveYesNo" onChange={handleChange} value={period.ActiveYesNo}>
                        <option value="0">No</option>
                        <option value="1">Yes</option>
                    </select>
                    <div className="form-actions">
                        <button className="btn" onClick={handleClick}>Update Period</button>
                        <button className="btn btn-secondary" type="button" onClick={handleImportTotals} disabled={importingTotals}>
                            {importingTotals ? "Importing..." : "Import Totals"}
                        </button>
                        <Link className="btn-link" to="/Periods">Cancel</Link>
                    </div>
                    {error && <p className="notice notice-error">Could not update period. Check input and try again.</p>}
                </div>
            </section>
        </div>
         );
    };

export default PeriodsUpdate;
